import { useState, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import CountryCode from 'react-phone-input-2';
import { Input, Select, TextArea, Button, Checkbox, Tooltip, Toggle } from 'common/components';
import { InfoIcon, PencilIcon, TrashIcon } from 'lucide-react';

import { WorkflowConfigFormData } from '../../AgenticWorkflowBuilder';
import { Controller, useFormContext } from 'react-hook-form';
import { WorkflowTagsType } from 'pages/WorkflowBuilder/WorkflowBuilder.types';
import { GenericTagsModal } from '../WorkflowGenericTagTabs/GenericTagsModal';
import { STANDARD_TIMEZONE_OPTIONS, STANDARD_TZ_VALUES } from 'common/constants';

const workflowTagsTypeOptions = [
    { value: WorkflowTagsType.PREDEFINED, label: 'Predefined' },
    { value: WorkflowTagsType.GENERIC, label: 'Generic' },
];

export const WorkflowConfigs = ({ isEdit }: { isEdit: boolean }) => {
    const [isGenericModalOpen, setIsGenericModalOpen] = useState(false);
    const {
        register,
        control,
        setValue,
        resetField,
        getValues,
        watch,
        formState: { errors },
    } = useFormContext<WorkflowConfigFormData>();
    const isGenericConfigured = getValues('isGenericConfigured');
    const operatingHoursEnabled = watch('operatingHoursEnabled');
    const currentTimezone = watch('operatingHoursTimezone');

    const timezoneOptions = useMemo(() => {
        const opts = [...STANDARD_TIMEZONE_OPTIONS];
        if (currentTimezone && !STANDARD_TZ_VALUES.has(currentTimezone) && currentTimezone !== 'OTHER') {
            opts.push({ value: currentTimezone, label: currentTimezone });
        }
        opts.push({ value: 'OTHER', label: 'Other' });
        return opts;
    }, [currentTimezone]);

    const handleResetGeneric = () => {
        setValue('isGenericConfigured', false);
        setValue('workflowTagsType', WorkflowTagsType.PREDEFINED);
        resetField('genericTags');
        resetField('genericPrompts');
    };

    return (
        <div>
            <h3>Workflow Configs</h3>
            <div className='mt-xl'>
                <Input
                    label='Workflow Name'
                    type='text'
                    error={!!errors.flowName}
                    errorText={errors.flowName?.message}
                    {...register('flowName', { required: 'Please enter a workflow name' })}
                    placeholder='Enter Workflow Name...'
                />
            </div>
            <div className='mt-lg'>
                <TextArea
                    label='Workflow Description'
                    error={errors.flowDescription?.message}
                    placeholder='Enter Workflow Description...'
                    rows={2}
                    resize='none'
                    {...register('flowDescription', { required: 'Please enter a workflow description' })}
                />
            </div>

            <div className='mt-lg flex items-center gap-sm'>
                <Controller
                    control={control}
                    name='isHumanTransfer'
                    render={({ field }) => (
                        <Checkbox
                            label='Enable human handoff'
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                        />
                    )}
                />
                <Tooltip
                    content='When enabled, calls are transferred to a human agent after successful disposition (i.e. once the AI has completed the conversation).'
                    position='top'
                >
                    <span className='flex items-center'>
                        <InfoIcon size={16} color='var(--primary-purple)' />
                    </span>
                </Tooltip>
            </div>

            <div className='mt-lg flex items-end gap-sm'>
                <Controller
                    control={control}
                    name='workflowTagsType'
                    render={({ field }) => (
                        <Select
                            label='Workflow tags type'
                            options={workflowTagsTypeOptions}
                            disabled={isEdit}
                            value={field.value}
                            onChange={(event) => {
                                field.onChange(event.target.value);
                                if (event.target.value === WorkflowTagsType.GENERIC) {
                                    setIsGenericModalOpen(true);
                                }
                            }}
                        />
                    )}
                />

                {isGenericConfigured && (
                    <div className='flex items-center gap-sm mt-md'>
                        <Button
                            variant='secondary'
                            icon={PencilIcon}
                            iconOnly
                            onClick={() => setIsGenericModalOpen(true)}
                        />
                        {!isEdit && <Button variant='danger' icon={TrashIcon} iconOnly onClick={handleResetGeneric} />}
                    </div>
                )}
            </div>

            <div className='mt-lg'>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Start Date</label>
                <Controller
                    control={control}
                    name='workflowStart'
                    render={({ field }) => (
                        <DatePicker
                            selected={field.value}
                            onChange={field.onChange}
                            dateFormat='yyyy-MM-dd'
                            placeholderText='Select start date'
                            isClearable
                        />
                    )}
                />
                <label style={{ display: 'block', marginBottom: '5px', marginTop: '10px', fontSize: '0.9rem' }}>
                    End Date
                </label>
                <Controller
                    control={control}
                    name='workflowEnd'
                    render={({ field }) => (
                        <DatePicker
                            selected={field.value}
                            onChange={field.onChange}
                            dateFormat='yyyy-MM-dd'
                            placeholderText='Select end date'
                            isClearable
                        />
                    )}
                />
            </div>
            <div className='mt-lg' style={{ display: 'none' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Country</label>
                <Controller
                    control={control}
                    name='countryCode'
                    render={({ field }) => (
                        <CountryCode
                            country={field.value}
                            value={field.value}
                            onChange={(value, country: any) => {
                                field.onChange(country.iso2);
                                setValue('dialCode', country.dialCode);
                            }}
                            inputStyle={{ width: '100%' }}
                            containerStyle={{ marginBottom: '10px' }}
                            inputProps={{
                                readOnly: true,
                                disabled: true,
                                placeholder: 'Select country',
                            }}
                            buttonStyle={{ background: '#f5f5f5' }}
                            dropdownStyle={{ width: '300px' }}
                            enableSearch={true}
                            disableSearchIcon={false}
                            searchPlaceholder='Search countries'
                        />
                    )}
                />
            </div>
            <div className='mt-lg' style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontSize: '0.9rem', fontWeight: 500 }}>
                    Operating Hours
                </label>
                <Controller
                    control={control}
                    name='operatingHoursEnabled'
                    render={({ field }) => (
                        <div className='flex items-center gap-sm'>
                            <Toggle
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                aria-label='Toggle 24 hour operating hours'
                            />
                            <span className='text-sm text-gray-700'>24 Hours</span>
                        </div>
                    )}
                />
                {!operatingHoursEnabled && (
                    <div className='mt-md'>
                        <div className='flex gap-sm'>
                            <div style={{ flex: 1 }}>
                                <Input
                                    label='Start Time'
                                    type='time'
                                    {...register('operatingHoursStartTime', {
                                        required: 'Start time is required',
                                    })}
                                    error={!!errors.operatingHoursStartTime}
                                    errorText={errors.operatingHoursStartTime?.message}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <Input
                                    label='End Time'
                                    type='time'
                                    {...register('operatingHoursEndTime', {
                                        required: 'End time is required',
                                        validate: (end) => {
                                            const start = getValues('operatingHoursStartTime');
                                            return !start || end > start || 'End time must be after start time';
                                        },
                                    })}
                                    error={!!errors.operatingHoursEndTime}
                                    errorText={errors.operatingHoursEndTime?.message}
                                />
                            </div>
                        </div>
                        <div className='mt-md'>
                            <div className='flex items-center gap-xs mb-xs'>
                                <label style={{ fontSize: '0.9rem' }}>Timezone</label>
                                <Tooltip
                                    content='Please reach out to support if you would like a custom timezone'
                                    position='top'
                                >
                                    <span className='flex items-center'>
                                        <InfoIcon size={14} color='var(--primary-purple)' />
                                    </span>
                                </Tooltip>
                            </div>
                            <Controller
                                control={control}
                                name='operatingHoursTimezone'
                                rules={{
                                    validate: (value) =>
                                        getValues('operatingHoursEnabled') ||
                                        value !== 'OTHER' ||
                                        'Please reach out to support for a custom timezone',
                                }}
                                render={({ field }) => (
                                    <Select
                                        options={timezoneOptions}
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        error={!!errors.operatingHoursTimezone}
                                        errorText={errors.operatingHoursTimezone?.message}
                                    />
                                )}
                            />
                        </div>
                    </div>
                )}
            </div>

            <GenericTagsModal
                isOpen={isGenericModalOpen}
                onClose={() => {
                    if (!getValues('isGenericConfigured')) {
                        handleResetGeneric();
                    }
                    setIsGenericModalOpen(false);
                }}
            />
        </div>
    );
};
